import { useState } from 'react'

import PencilAltIcon from '@heroicons/react/outline/PencilAltIcon'
import PhotographIcon from '@heroicons/react/outline/PhotographIcon'
import ShieldExclamationIcon from '@heroicons/react/outline/ShieldExclamationIcon'
import ViewGridIcon from '@heroicons/react/outline/ViewGridIcon'
import { useDecision } from '@optimizely/react-sdk'
import { Link } from 'react-router-dom'

import {
  Space,
  SpaceListOrderByEnum,
  SpaceType,
} from '@tribeplatform/gql-client/types'
import { useSpaces } from '@tribeplatform/react-sdk/hooks'
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'

import { FeaturesList } from '../../../lib/optimizely/feature.list'
import { useDebounce } from '../../../utils/useDebounce'
import { AuditLogsModal } from '../../AuditLog/AuditLogsModal'
import { CreateSpaceModal } from '../../Space/CreateSpaceModal'
import { Table } from '../../Table'
import { SpaceSettingsModal } from './SpaceSettingsModal'

export const Spaces = () => {
  const [createSpaceModal, setCreateSpaceModal] = useState<boolean>()
  const [editSpaceModal, setEditSpaceModal] = useState<string | null>()
  const [auditLogsModal, setAuditLogsModal] = useState<string>()

  const [filter, setFilter] = useState({ query: undefined })
  const debouncedQuery = useDebounce(filter?.query, 300)
  const [orderBy, setOrderBy] = useState({
    column: SpaceListOrderByEnum.CREATED_AT,
    reverse: true,
  })

  const [decisionAuditLogs] = useDecision(FeaturesList.NewModerationPanel)

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useSpaces({
      fields: {
        image: 'basic',
        collection: 'basic',
        postsCount: 'basic',
      },
      variables: {
        limit: 15,
        query: debouncedQuery,
        orderBy: orderBy.column,
        reverse: orderBy.reverse,
        type: [SpaceType.GROUP],
      },
      useInfiniteQueryOptions: { keepPreviousData: true },
    })

  const spaces = simplifyPaginatedResult<Space>(data)

  const columns = [
    {
      accessor: 'image',
      renderer: 'spaceImage',
      Header: (
        <div className="w-8">
          <PhotographIcon className="w-5 h-5 m-auto" />
        </div>
      ),
      visible: true,
      width: 'collapse',
    },
    {
      Header: 'ID',
      accessor: 'id',
      renderer: 'spacePrimary',
      visible: false,
      width: 'sm',
    },
    {
      Header: 'Name',
      accessor: 'name',
      renderer: 'spacePrimary',
      visible: true,
      width: 'md',
    },
    {
      Header: 'Members Count',
      accessor: 'membersCount',
      renderer: 'number',
      visible: true,
      width: 'sm',
    },
    {
      Header: 'Posts Count',
      accessor: 'postsCount',
      renderer: 'number',
      visible: true,
      width: 'sm',
    },
    {
      Header: 'Slug',
      accessor: 'slug',
      renderer: 'spacePrimary',
      visible: false,
      width: 'sm',
    },
    {
      Header: 'Created At',
      accessor: 'createdAt',
      renderer: 'date',
      visible: true,
      orderKey: SpaceListOrderByEnum.CREATED_AT,
      width: 'sm',
    },
    {
      Header: 'Private',
      accessor: 'private',
      renderer: 'boolean',
      visible: true,
      width: 'xs',
    },
    {
      Header: 'Invite Only',
      accessor: 'inviteOnly',
      renderer: 'boolean',
      visible: false,
      width: 'xs',
    },
    {
      Header: 'Hidden',
      accessor: 'hidden',
      renderer: 'boolean',
      visible: false,
      width: 'xs',
    },
    {
      Header: 'Description',
      accessor: 'description',
      renderer: 'longtext',
      visible: false,
      width: 'xl',
    },
    {
      visible: true,
      accessor: 'actions',
      locked: true,
      width: 'xs',
      renderer: (space: Space) => (
        <Dropdown>
          <Dropdown.ButtonMinimal />
          <Dropdown.Items>
            <Dropdown.Item
              as={Link}
              to={`/${space?.slug}`}
              leadingIcon={<ViewGridIcon />}
            >
              View space
            </Dropdown.Item>
            <Dropdown.Item
              leadingIcon={<PencilAltIcon />}
              onClick={() => {
                setEditSpaceModal(space.id)
              }}
            >
              Edit space
            </Dropdown.Item>
            {decisionAuditLogs?.enabled && (
              <Dropdown.Item
                leadingIcon={<ShieldExclamationIcon />}
                onClick={() => setAuditLogsModal(space?.id)}
              >
                Audit log
              </Dropdown.Item>
            )}
          </Dropdown.Items>
        </Dropdown>
      ),
    },
  ]

  return (
    <div className="w-full">
      <Table
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        columns={columns}
        data={data}
        setFilter={setFilter}
        filter={filter}
        orderBy={orderBy}
        title="Spaces"
        setOrderBy={setOrderBy}
        actions={
          <>
            <Button
              size="lg"
              variant="primary"
              onClick={() => setCreateSpaceModal(true)}
            >
              Add space
            </Button>
          </>
        }
      />
      {createSpaceModal && (
        <CreateSpaceModal open onClose={() => setCreateSpaceModal(false)} />
      )}
      {editSpaceModal && (
        <SpaceSettingsModal
          open
          onClose={() => setEditSpaceModal(null)}
          space={spaces?.nodes?.find(s => s.id === editSpaceModal)}
        />
      )}
      {auditLogsModal && (
        <AuditLogsModal
          open
          onClose={() => setAuditLogsModal(null)}
          spaceId={auditLogsModal}
        />
      )}
    </div>
  )
}
